import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, DollarSign, Clock, CheckCircle2, 
  XCircle, Search, Filter, RefreshCw, Trash2, Edit, 
  Phone, Mail, Home, ArrowLeft, Lock, Eye, EyeOff,
  TrendingUp, Users, AlertCircle, Download, Paperclip
} from 'lucide-react';
import { Logo } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Edit modal
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newPaymentStatus, setNewPaymentStatus] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        toast.success('Login successful');
        fetchData();
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch stats
      const statsResponse = await axios.get(`${API}/admin/stats`);
      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
      }
      
      // Fetch submissions
      await fetchSubmissions();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/admin/submissions?${params.toString()}`);
      if (response.data.success) {
        setSubmissions(response.data.submissions);
        setTotalSubmissions(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubmissions();
    }
  }, [statusFilter, paymentFilter, searchQuery, isAuthenticated]);

  const handleUpdateStatus = async () => {
    if (!editingSubmission) return;
    
    try {
      const response = await axios.patch(
        `${API}/admin/submissions/${editingSubmission.id}`,
        {
          status: newStatus || editingSubmission.status,
          payment_status: newPaymentStatus || editingSubmission.payment_status
        }
      );
      
      if (response.data.success) {
        toast.success('Submission updated');
        setEditingSubmission(null);
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const response = await axios.delete(`${API}/admin/submissions/${id}`);
      if (response.data.success) {
        toast.success('Submission deleted');
        fetchData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || colors.pending;
  };

  const getPaymentColor = (status) => {
    const colors = {
      pending: 'bg-orange-100 text-orange-800 border-orange-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
      invoice_sent: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[status] || colors.pending;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8" data-testid="admin-login-card">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Enter password to access</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-gray-700 font-semibold mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12 pr-12"
                  data-testid="admin-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold"
              data-testid="admin-login-btn"
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              ← Back to Home
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="text-gray-300">|</span>
            <span className="font-semibold text-gray-700">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={isLoading}
              className="flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              onClick={() => setIsAuthenticated(false)}
              className="text-gray-600"
              data-testid="logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="p-6" data-testid="stat-total">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Submissions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_submissions}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6" data-testid="stat-pending">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Review</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.by_status.pending}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6" data-testid="stat-revenue">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Revenue Collected</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.revenue.collected.toLocaleString()}</p>
                </div>
              </div>
            </Card>
            
            <Card className="p-6" data-testid="stat-potential">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">${stats.revenue.pending.toLocaleString()}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Status Summary */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-yellow-700">{stats.by_status.pending}</p>
              <p className="text-sm text-yellow-600">Pending</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{stats.by_status.in_progress}</p>
              <p className="text-sm text-blue-600">In Progress</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{stats.by_status.completed}</p>
              <p className="text-sm text-green-600">Completed</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{stats.by_status.cancelled}</p>
              <p className="text-sm text-red-600">Cancelled</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Label className="text-gray-700 font-medium mb-2 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, address, invoice..."
                  className="pl-10 h-11"
                  data-testid="admin-search-input"
                />
              </div>
            </div>
            
            <div className="w-full lg:w-48">
              <Label className="text-gray-700 font-medium mb-2 block">Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-gray-200 bg-white"
                data-testid="admin-status-filter"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div className="w-full lg:w-48">
              <Label className="text-gray-700 font-medium mb-2 block">Payment</Label>
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-gray-200 bg-white"
                data-testid="admin-payment-filter"
              >
                <option value="">All Payments</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="invoice_sent">Invoice Sent</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Submissions Table */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-bold text-gray-900">
              Submissions ({totalSubmissions})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="submissions-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Invoice</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Property</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Listing Agent</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Service</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Payment</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">File</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {submissions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-12 text-center text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      No submissions found
                    </td>
                  </tr>
                ) : (
                  submissions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50" data-testid={`submission-row-${sub.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-gray-900">{sub.invoice_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">{sub.property_address}</p>
                          <p className="text-gray-500">{sub.property_city}, {sub.property_state} {sub.property_zip}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">{sub.listing_agent_name}</p>
                          <p className="text-gray-500">{sub.listing_agent_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{sub.service_type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(sub.status)}`}>
                          {sub.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getPaymentColor(sub.payment_status)}`}>
                          {sub.payment_status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {sub.uploaded_file_id ? (
                          <a
                            href={`${BACKEND_URL}${sub.uploaded_file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 text-sm font-medium"
                            title={sub.uploaded_file}
                            data-testid={`download-file-${sub.id}`}
                          >
                            <Download className="w-4 h-4" />
                            <span className="max-w-[100px] truncate">{sub.uploaded_file}</span>
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">No file</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{sub.submission_date}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingSubmission(sub);
                              setNewStatus(sub.status);
                              setNewPaymentStatus(sub.payment_status);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                            data-testid={`edit-btn-${sub.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(sub.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                            data-testid={`delete-btn-${sub.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Edit Modal */}
      {editingSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg p-6" data-testid="edit-modal">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Update Submission</h3>
            
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500">Invoice</p>
                <p className="font-mono font-medium">{editingSubmission.invoice_number}</p>
              </div>
              
              <div>
                <Label className="text-gray-700 font-medium mb-2 block">Status</Label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-gray-200 bg-white"
                  data-testid="edit-status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div>
                <Label className="text-gray-700 font-medium mb-2 block">Payment Status</Label>
                <select
                  value={newPaymentStatus}
                  onChange={(e) => setNewPaymentStatus(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-gray-200 bg-white"
                  data-testid="edit-payment-select"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="invoice_sent">Invoice Sent</option>
                </select>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEditingSubmission(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStatus}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="save-changes-btn"
              >
                Save Changes
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Admin;
